import {
  BookOption,
  PurchaseBookOption,
  TypeOfBookValue,
} from "@app/constants";
import { Author } from "./author.type";

export enum BookMediaType {
  AUDIO = 1,
  EBOOK = 2,
  AUDIO_EBOOK = 3,
}

export interface Book {
  id: number;
  name: string;
  description: string;
  price: number;
  bookOption: BookOption;
  musicId: number;
  thumbnails: string[];
  author: string;
  categoryId: number;
  listenPercent: number;
  isPopular: boolean;
  isAvailable: boolean;
  categoryName: string | null;
  available: boolean;
  slideName?: string;
  insight: {
    likeCount: number;
    viewCount: number;
  };
}

export interface BookElementChildren {
  id?: number;
  name: string;
  description: string;
  audio?: string;
  audioName?: string;
  content: string;
}

export interface BookElement {
  id?: number;
  name: string;
  description: string;
  audio?: File;
  audioName?: string;
  content?: string;
  children?: BookElementChildren[];
}

export interface CreateBook {
  name: string;
  categoryId: number;
  musicId: number;
  bookOption: string;
  price: number;
  author: string;
  description: string;
  thumbnails: File[];
  isPopular: boolean;
  elements: BookElement[];
}

export interface CreateBookForm {
  name: string;
  categoryId: number;
  musicId: number;
  bookOption: string;
  price: string;
  author: string;
  description: string;
  isPopular: string[];
  elements: BookElement[];
}

export interface BookElementChildrenDetail {
  id: number;
  name: string;
  description: string | null;
  content: string;
  linkAudio: string;
  level: number;
  order: number;
  child?: BookElementChildrenDetail[];
  isLock?: boolean;
  point?: number;
  bookNotes?: BookNoteDetail[];
  audioDuration?: number;
  audios?: BookAudioDetail[];
  highlightTexts?: BookElementHighlightTextDetail[];
}

export interface BookNoteDetail {
  id: number;
  textSelected: string;
  textNote: string;
}

export interface BookAudioDetail {
  name?: string;
  audio?: string;
  endpointConvertId?: number;
}

export interface BookElementHighlightTextDetail {
  text?: string;
  duration?: number;
  from?: number;
  to?: number;
  mediaLink?: string;
  mediaType?: string;
}

export interface BookDetailAuthor {
  id: number;
  fullName: string;
  thumbnail: string;
}

export interface BookElementDetail {
  id: number;
  name: string;
  description: string | null;
  content: string;
  linkAudio: string;
  level: number;
  order: number;
  child: BookElementChildrenDetail[];
  isLock: boolean;
  point: number;
  bookNotes: BookNoteDetail[];
  audioDuration?: number;
  audios?: BookAudioDetail[];
  highlightTexts?: BookElementHighlightTextDetail[];
}

export interface BookDetail {
  id: number;
  available: boolean;
  linkStore: string;
  categories: {
    categoryId: number;
    categoryName: string;
    categoryTitle: string | null;
  }[];
  backgroundMusic: {
    id: number;
    name: string;
    link: string;
  };
  name: string;
  description: string;
  price: number;
  oldPrice?: number;
  bookOption: BookOption;
  thumbnails: string[];
  author: string;
  authors?: BookDetailAuthor[];
  isPopular: boolean;
  isFavorite: boolean;
  isTryListening: boolean;
  isAvailable: boolean;
  releaseType?: number;
  totalPoint?: number;
  totalTime?: number;
  elements: BookElementDetail[];
  top30Book?: Book[];
  insight: {
    likeCount: number;
    commentCount: number;
    listenCount: number;
    shareCount: number;
    viewCount: number;
  };
  mediaType?: BookMediaType;
  audio?: BookAudioAccessInfo;
  ebook?: BookEbookAccessInfo;
}

export interface BookAudioAccessInfo {
  enabled: boolean;
  hasAccess: boolean;
  canPurchase: boolean;
  price: number;
  oldPrice?: number;
  totalTime?: number;
  totalPoint?: number;
  isTryListening?: boolean;
  productId?: string;
}

export interface BookEbookAccessInfo {
  enabled: boolean;
  hasAccess: boolean;
  canPurchase: boolean;
  price: number;
  pageCount?: number;
  status?: string;
}

export interface BookByCategory {
  categoryId: number;
  categoryName: string;
  items: Book[];
}

export interface BooksByTags {
  sliders: Book[];
  bookFree: Book[];
  topBookReComment: Book[];
  latestRelease: Book[];
  bookByCategory: BookByCategory[];
  authors: Author[];
  comingSoon: Book[];
  trending: Book[];
}

export type TypeOfBook = (typeof TypeOfBookValue)[keyof typeof TypeOfBookValue];

export interface PurchaseOption {
  bookId: number;
  purchaseBookOption: PurchaseBookOption;
}

export interface EbookSession {
  token: string;
  expiredAt: number;
  bookEbookFileId: number;
}

export interface EbookManifestPage {
  page: number;
  resourceId: number;
}

export interface EbookManifest {
  bookId: number;
  bookEbookFileId: number;
  pageCount: number;
  expiredAt: number;
  pages: EbookManifestPage[];
}

export interface EbookContent {
  resourceId: number;
  page: number;
  content: string;
}

export interface EbookProgress {
  id: number;
  bookId: number;
  bookEbookFileId: number;
  currentPage: number;
  progressPercent: number;
  lastPositionJson?: Record<string, unknown>;
}

export interface EbookNote {
  id: number;
  bookId: number;
  bookEbookFileId: number;
  pageNo: number;
  textSelected: string;
  textNote: string;
  anchorJson?: Record<string, unknown>;
  createdDate: number;
  lastModifiedDate: number;
}

export enum SortOption {
  POSTED_DATE = 1,
  A_TO_Z = 2,
  POPULAR = 3,
}

export enum BookOptionOption {
  FREE = 1,
  VIP = 2,
}

export interface GetBookOptionRequestParams {
  pageNum: number;
  pageSize: number;
  sortOption: SortOption;
  bookOption?: BookOptionOption;
}
